import { matchPath } from "react-router-dom";
import { ROUTES } from "@/lib/constants";

/* Where the shell's own Back arrow (TopBar / PublicNavbar) appears.

   Every page that is not a top-level destination gets one, so a person who
   followed a link — from a notification, a profile, an email — always has a
   way out in the same place. Two kinds of route are excluded:

   - Tabs. The bottom nav / sidebar IS the navigation between them; an arrow
     on a tab root would compete with it and, on a first visit, have nowhere
     meaningful to go.
   - Pages that draw their own Back inside the content (quest, task, chooser,
     bounty, workspace chat). Theirs carries page-specific wording and
     fallbacks ("Back to the quest"); a second arrow in the bar above it would
     be two controls for one action.

   Patterns, not prefixes: /quest/:id owns its Back but a hypothetical
   /quest/:id/something-new would not, and should get the shell's. */
const TAB_ROUTES: readonly string[] = [
  ROUTES.ASSISTANT,
  ROUTES.CHATS,
  ROUTES.DISCOVER,
  ROUTES.WORK,
  ROUTES.PROFILE,
  ROUTES.ADMIN,
];

const PAGE_OWNS_BACK: readonly string[] = [
  ROUTES.QUEST_DETAIL,
  "/q/:id",
  "/q/:id/apply",
  "/quest/:id/apply",
  ROUTES.TASK_DETAIL,
  "/t/:taskId",
  ROUTES.BOUNTY_DETAIL,
  ROUTES.ASSISTANT_WORKSPACE,
];

/** Whether the shell should render its Back arrow on `pathname`. */
export function shouldShowShellBack(pathname: string): boolean {
  if (pathname === ROUTES.HOME) return false;
  if (TAB_ROUTES.includes(pathname)) return false;
  return !PAGE_OWNS_BACK.some((pattern) => matchPath(pattern, pathname));
}

/**
 * Where Back goes when the page is the tab's first entry (opened from a link,
 * so there is no in-app page behind it): the app's home for a signed-in
 * person, the landing page for a visitor.
 */
export function shellBackFallback(isAuthenticated: boolean): string {
  return isAuthenticated ? ROUTES.DISCOVER : ROUTES.HOME;
}
